import {
  CampaignPhase,
  CampaignPostDTO,
  CampaignStatus,
  ContentKind,
  DraftLanguage,
  PostRole,
  PostStatus,
  PostTextAction,
  PostsNotPublishableDTO,
  StoryStatus
} from 'models/socialCampaign'

import { AnalyticsEvent } from './AnalyticsEvent'
import axios from 'axios'
import { logAnalyticsEvent } from './analyticsUtils'

/**
 * How the backend answered an action of the tab. A refused write publishes no server event (ISSUE-3620), so the page
 * is the only place a refusal is counted.
 */
export type SocialCampaignOutcome = 'ok' | 'not_publishable' | 'conflict' | 'failed'

/**
 * Where the post editor was opened from: `address` is a reload, a shared link or Back / Forward onto `#post=…`, `other`
 * an opening that does not say where it comes from.
 */
export type SocialCampaignEditorSource = 'row' | 'review_all' | 'action_bar' | 'empty_state' | 'address' | 'other'

/** `undo_text`: Undo after an AI text action, the text it replaced saved back */
export type SocialCampaignPostAction = 'approve' | 'launch' | 'undo_approval' | 'remove' | 'create' | 'edit' | 'undo_text'

/** The posts a 422 named, counted, and their reasons once each (never their subjects: they come from the posts' text). */
interface RefusalProperties {
  refusedPosts?: number
  refusedReasons?: string
}

/** The post an action is about, by id and enums only. */
interface PostProperties {
  postId?: string
  postRole?: PostRole
  postStatus?: PostStatus
}

/** How the backend answered a write, with its HTTP status on a failure (0 when no answer came back). */
interface OutcomeProperties {
  outcome: SocialCampaignOutcome
  httpStatus?: number
}

/**
 * Properties per event. Values are flat scalars: the body goes to every provider (`logAnalyticsEvent`), and GA,
 * Firebase and Intercom drop lists and objects, so a list is a comma-joined string. Never a post's text or subject, a
 * caption, a name or an address: ids, enums, counts and booleans only.
 */
export interface SocialCampaignEventProperties {
  /**
   * The tab shown, once per visit of a property's tab, entered by a click, a link or a reload; `loadFailed` when the
   * campaign could not be read (the tab shows its load error)
   */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_TAB_VIEWED]: { loadFailed?: boolean }
  /** "Start marketing campaign"; `retry` after a failed generation */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_STARTED]: { draftLanguage: DraftLanguage, retry: boolean }
  /**
   * The generation over: the draft shown (`created`, or `existing` when the property already had a campaign), back
   * to the intro (`failed`, with how the creation was answered), or the client gone before the draft showed
   * (`abandoned`). `storyWaitTimedOut` when the draft showed before its story was written.
   */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATION_FINISHED]: {
    outcome: 'created' | 'existing' | 'failed' | 'abandoned'
    /** From the click to the draft shown, the step animation and the story wait included */
    durationMs: number
    /** From the click to the creation's answer: the backend's part of `durationMs` */
    createMs?: number
    /** Failed: `conflict` is a 409 whose campaign could not be read either */
    failure?: SocialCampaignOutcome
    httpStatus?: number
    storyStatus?: StoryStatus | 'NONE'
    storyWaitTimedOut?: boolean
  }
  /** The plan sentence sent (the drafts re-planned), and how it was answered */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_PLAN_SAVED]: OutcomeProperties & { posts: number, weeks: number, previousPosts: number, previousWeeks: number }
  /** A kind of content kept or left out, sent, and how it was answered */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_CONTENT_KIND_SAVED]: OutcomeProperties & { contentKind: ContentKind, included: boolean }
  /** A way chosen in a kind's Add menu: Order opens the purchase flow; Create with AI and Upload until their tickets wire them */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_CONTENT_ADD_CHOSEN]: { contentKind: ContentKind, way: 'create_with_ai' | 'order' | 'upload' }
  /** A change of the networks & language panel sent (`changed`: networks, language), with its values and how it was answered */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_SETTINGS_SAVED]: OutcomeProperties & {
    changed: string
    networks?: string
    networkCount?: number
    draftLanguage?: DraftLanguage
  }
  /** The post editor opened (a walk to another post is not an opening) */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_EDITOR_OPENED]: PostProperties & { source: SocialCampaignEditorSource, mode: 'review' | 'edit' | 'create' }
  /**
   * The bar's Approve all, or Launch campaign once every draft is approved; `drafts` and `approvedBefore` as the bar
   * counted them (a launched campaign's draft whose time passed is not one)
   */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_APPROVE_ALL_SUBMITTED]: RefusalProperties & OutcomeProperties & {
    control: 'approve_all' | 'launch'
    drafts: number
    approvedBefore: number
    /** Drafts the answer scheduled */
    scheduled?: number
    /** Posts waiting for ordered content, on a launch */
    waiting?: number
    /** The answer took a draft campaign live */
    launched: boolean
  }
  /** "Generate new posts" on a launched campaign; `added` drafts in the answer */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_GENERATE_SUBMITTED]: OutcomeProperties & { requestedPosts: number, weeks: number, added?: number }
  /** Pause, resume, end or reopen the campaign, with what the page told the client it would hold, miss or drop */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_LIFECYCLE_SUBMITTED]: OutcomeProperties & {
    action: 'pause' | 'resume' | 'end' | 'reopen'
    /** Resume: the header's switch or the paused message */
    source?: 'switch' | 'message'
    heldPosts?: number
    missedPosts?: number
    postsAhead?: number
  }
  /** An action of the post editor, `edit` being a save of what changed (`changedFields`), on the editor's buttons or as it goes */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_POST_ACTION_SUBMITTED]: PostProperties & RefusalProperties & OutcomeProperties & {
    action: SocialCampaignPostAction
    /** The answer took a draft campaign live (the last draft decided) */
    launched?: boolean
    changedFields?: string
    /** Edit: sent by the editor's own buttons, or as it went without them (Back, another tab of the property) */
    via?: 'editor' | 'leave'
    mediaCount?: number
    networkCount?: number
    /** `undo_text`: the AI action whose text Undo replaced */
    textAction?: PostTextAction
  }
  /**
   * Regenerate, Shorter or More hashtags asked on a post's text (ISSUE-3619), as the request was answered: `ok` once the
   * text is PENDING, `refused` for a 400 (nothing to rewrite, no room left). A save the action needed first and that
   * failed is the `edit` it was, and asks nothing.
   */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_TEXT_ACTION_SUBMITTED]: PostProperties & {
    textAction: PostTextAction
    outcome: SocialCampaignOutcome | 'refused'
    httpStatus?: number
  }
  /**
   * What became of an action asked from this page, read after a read showed it PENDING: `written` (READY) or `failed`
   * (FAILED). Once per request; none for an action let go before a read showed it PENDING (its text moved on), an answer
   * read on a reload or asked from another tab, nor once the client left the tab before it came.
   */
  [AnalyticsEvent.SOCIAL_CAMPAIGN_TEXT_ACTION_FINISHED]: PostProperties & {
    textAction: PostTextAction
    outcome: 'written' | 'failed'
    /**
     * From the backend taking the action (its 202) to the answer read, the poll's pace included. The page sees no
     * timeout of its own: the backend fails an action unanswered after 5 minutes, so a `failed` at about that is one.
     */
    durationMs: number
  }
}

export type SocialCampaignAnalyticsEvent = keyof SocialCampaignEventProperties

export interface SocialCampaignTrackingTarget {
  workspaceId: string
  realtyId: string
}

/** The campaign as the page had it when the client acted; none before the property has one. */
export type SocialCampaignTrackingCampaign = { id: string, phase: CampaignPhase } | null | undefined

/**
 * Logs an event of the Marketing campaign tab (ISSUE-3637) with the ids it is about: the workspace and the property,
 * the campaign and its phase when there is one (`NONE` before). Ids are explicit on each event rather than super
 * properties: the order flow owns the `realtyId` super property and unregisters it when it closes.
 */
export const trackSocialCampaignEvent = <E extends SocialCampaignAnalyticsEvent>(
  event: E,
  target: SocialCampaignTrackingTarget,
  campaign: SocialCampaignTrackingCampaign,
  properties: SocialCampaignEventProperties[E]
) => {
  const body: Record<string, string | number | boolean> = {
    workspaceId: target.workspaceId,
    realtyId: target.realtyId,
    campaignPhase: campaign?.phase ?? 'NONE',
  }
  if (campaign) body.campaignId = campaign.id
  for (const [name, value] of Object.entries(properties as Record<string, string | number | boolean | undefined>)) {
    if (value !== undefined) body[name] = value
  }
  logAnalyticsEvent(event, body)
}

/** The outcome of an action that did not go through. */
export const failureOutcomeOf = (failure: unknown): SocialCampaignOutcome => {
  const status = axios.isAxiosError(failure) ? failure.response?.status : undefined
  if (status === 422) return 'not_publishable'
  if (status === 409) return 'conflict'
  return 'failed'
}

/** The HTTP status of a failure, 0 when no answer came back (network error, timeout). */
export const httpStatusOf = (failure: unknown): number =>
  axios.isAxiosError(failure) ? failure.response?.status ?? 0 : 0

/** What the analytics say of a write that did not go through: its outcome and HTTP status (no text, no message). */
export const failurePropertiesOf = (failure: unknown): Required<OutcomeProperties> => ({
  outcome: failureOutcomeOf(failure),
  httpStatus: httpStatusOf(failure),
})

export const refusalPropertiesOf = (refusal: PostsNotPublishableDTO | null): RefusalProperties => {
  if (!refusal) return {}
  const reasons = new Set(refusal.posts.flatMap((post) => post.reasons))
  return { refusedPosts: refusal.posts.length, refusedReasons: Array.from(reasons).sort().join(',') }
}

/** A list of enum values as one property (see SocialCampaignEventProperties). */
export const joinValues = (values: readonly string[]) => values.join(',')

/** The post an action is about, by its id and enums: never its subject nor its text. */
export const trackedPostOf = (post: CampaignPostDTO | null | undefined): PostProperties =>
  post ? { postId: post.id, postRole: post.role, postStatus: post.status } : {}

/** The answer took a draft campaign live. */
export const wentLive = (before: { status: CampaignStatus }, answer: { status: CampaignStatus } | null | undefined) =>
  before.status === CampaignStatus.DRAFT && answer?.status === CampaignStatus.LIVE
